import React, { useEffect, useState } from "react";
import { Link2, Save } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { Btn, Choice, Field, Modal, SecretBox, inputCls } from "./kit";

const FIELDS = ["first_name", "last_name", "email", "phone", "organization", "notes", "type", "status"];

export const PartnerEditDialog = ({ partnerId, meta, onClose, onSaved }) => {
  const [orig, setOrig] = useState(null);
  const [f, setF] = useState({});
  const [perms, setPerms] = useState({});
  const [link, setLink] = useState("");
  const load = () => apiClient.get(`/console/partners/${partnerId}`).then(({ data }) => {
    setOrig(data); setF(data.partner); setPerms(Object.fromEntries(Object.entries(data.permissions).map(([k, v]) => [k, v.granted])));
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [partnerId]);
  if (!orig) return null;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    const body = Object.fromEntries(FIELDS.filter((k) => (f[k] ?? "") !== (orig.partner[k] ?? "")).map((k) => [k, f[k]]));
    const changed = Object.fromEntries(Object.entries(perms).filter(([k, v]) => v !== orig.permissions[k].granted));
    if (Object.keys(changed).length) body.permissions = changed;
    if (!Object.keys(body).length) return toast.message("Nothing changed");
    try { await apiClient.patch(`/console/partners/${partnerId}`, body); toast.success("Saved"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetDefault = async (key) => {
    try { await apiClient.patch(`/console/partners/${partnerId}`, { permissions: { [key]: null } }); toast.success(`${key} reset to type default`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const issueLink = async () => {
    try { setLink((await apiClient.post(`/console/partners/${partnerId}/reset-access`)).data.invite_url); toast.success("PIN setup link issued"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Modal open onClose={onClose} title={`Edit partner · ${orig.partner.first_name} ${orig.partner.last_name}`} testId="partner-edit-dialog">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          {["first_name", "last_name", "email", "phone", "organization"].map((k) => (
            <Field key={k} label={k.replace("_", " ")}><input data-testid={`partner-${k}`} className={inputCls} value={f[k] || ""} onChange={set(k)} /></Field>
          ))}
          <Field label="Partner type"><Choice testId="partner-type" value={f.type} onChange={set("type")} options={meta.partner_types} /></Field>
          <Field label="Account status"><Choice testId="partner-status" value={f.status} onChange={set("status")} options={meta.partner_statuses} /></Field>
        </div>
        <Field label="Notes"><textarea data-testid="partner-notes" rows={2} className={inputCls} value={f.notes || ""} onChange={set("notes")} /></Field>
        <div className="space-y-2">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">Portal permissions</div>
          {Object.entries(orig.permissions).map(([key, p]) => (
            <div key={key} className="flex items-center gap-3 p-2 border border-white/10 rounded-sm">
              <input type="checkbox" data-testid={`partner-perm-${key}`} checked={!!perms[key]} onChange={() => setPerms((x) => ({ ...x, [key]: !x[key] }))} />
              <div className="flex-1"><div className="font-mono text-[11px] text-white">{key}</div><div className="text-[11px] text-white/50">{p.label}</div></div>
              <span className="font-mono text-[9px] uppercase text-white/40">{p.source === "override" ? "override" : "type default"}</span>
              {p.source === "override" && <button type="button" data-testid={`partner-perm-reset-${key}`} onClick={() => resetDefault(key)} className="text-[10px] text-[#d4a017] hover:underline">reset</button>}
            </div>
          ))}
        </div>
        {link && <SecretBox label="PIN setup link (72h)" value={link} testId="partner-reset-link" />}
        <div className="flex justify-between gap-2 pt-2">
          <Btn tone="muted" data-testid="partner-reset-access" onClick={issueLink}><Link2 size={12} /> PIN reset link</Btn>
          <Btn data-testid="partner-save" onClick={save}><Save size={12} /> Save changes</Btn>
        </div>
      </div>
    </Modal>
  );
};
